import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faWarning } from "@fortawesome/pro-regular-svg-icons";

import { RECONNECT_LINKEDIN_DEEP_LINK } from "@/pages/Private/pages/Account/linkedinDeepLink";

import { Icon } from "../Icon/Icon";
import { Tooltip } from "../Tooltip/Tooltip";

interface SidebarConnectionWarningsProps {
	/**
	 * LinkedIn is allowed for the user but not currently connected. Optional/`undefined`
	 * (the shape `useTopbarInfo` returns when the user isn't loaded) is treated as "don't show".
	 */
	isLinkedinNotConnected?: boolean;
	/** Sales Navigator licence is held but its session is disconnected. */
	isSalesNavigatorNotConnected?: boolean;
	/** Render the compact icon-only variant used by the collapsed sidebar. */
	collapsed?: boolean;
}

/**
 * The LinkedIn / Sales Navigator "not connected" warning chips shown in the sidebar. Clicking one
 * navigates to the Account page and auto-opens the LinkedIn connect/reconnect flow (see
 * {@link RECONNECT_LINKEDIN_DEEP_LINK}). Extracted from `Sidebar.tsx` so the warning + its
 * deep-link behaviour live in one place and both sidebar layouts reuse it.
 */
export const SidebarConnectionWarnings: FunctionComponent<SidebarConnectionWarningsProps> = ({
	isLinkedinNotConnected,
	isSalesNavigatorNotConnected,
	collapsed = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`sidebar.${key}`);
	const navigate = useNavigate();

	const goToReconnect = () => navigate(RECONNECT_LINKEDIN_DEEP_LINK);

	const warnings = [
		{ id: "linkedin", show: isLinkedinNotConnected, suffix: "LinkedIn" },
		{ id: "salesNav", show: isSalesNavigatorNotConnected, suffix: "SalesNav" },
	].filter((warning) => warning.show);

	if (collapsed) {
		return (
			<>
				{warnings.map(({ id, suffix }) => (
					<div key={id} className="cursor-pointer" onClick={goToReconnect}>
						<Tooltip content={ts(`connectSignalist${suffix}`)}>
							<div className="w-[24px] h-[24px] rounded-full border border-text-warning font-bold bg-background-warning flex items-center justify-center">
								<Icon className="w-[12px] h-[12px] text-text-warning" icon={faWarning} />
							</div>
						</Tooltip>
					</div>
				))}
			</>
		);
	}

	return (
		<>
			{warnings.map(({ id, suffix }) => (
				<Tooltip key={id} content={ts(`clickToConnect${suffix}`)} direction="bottom">
					<div
						className="relative text-sm items-center cursor-pointer rounded-xl border border-text-warning bg-background-warning  p-2 z-10  whitespace-normal break-words min-w-fit"
						onClick={goToReconnect}
					>
						<span className=" text-text-warning flex justify-center w-full h-full items-center gap-2 ">
							<Icon className="w-[16px] h-[16px] text-text-warning" icon={faWarning} />
							<span>{ts(`connectSignalist${suffix}`)}</span>
						</span>
					</div>
				</Tooltip>
			))}
		</>
	);
};
